import React from "react";
import * as Controls from "./components";
import { Formik, Form as FormikForm, FormikProps } from "formik";
import { FormProps as StrapFormProps } from "reactstrap";
import * as yup from "yup";

type FormValues = object;

type BaseStrapFormProps = Omit<StrapFormProps, "children">;
export interface FormProps<TValues extends object = object>
    extends BaseStrapFormProps {
    initialValues?: TValues;
    validationSchema?: yup.ObjectSchema<TValues> | yup.Schema<TValues>;
    isSubmitting?: boolean;
    enableReinitialize?: boolean;
    children?:
    | React.ReactNode
    | ((formik: FormikProps<TValues>) => React.ReactNode);
    formikRef?: React.Ref<FormikProps<TValues>>;
    onSubmit?: (values: TValues) => void;
}

export interface FormDialogProps<TValues extends FormValues = FormValues> {
    children: React.ReactNode;
    initialValues?: TValues;
    validationSchema?: yup.ObjectSchema<TValues> | yup.Schema<TValues>;
}

const pickFormDomProps = <TValues extends FormValues>(
    props: FormProps<TValues>
): BaseStrapFormProps => {
    const {
        initialValues,
        validationSchema,
        enableReinitialize,
        onSubmit,
        children,
        formikRef,
        ...rest
    } = props;
    return rest;
};

const Form = <TValues extends FormValues = FormValues>(
    props: FormProps<TValues>
) => {
    const _initialValues = (props.initialValues ?? {}) as TValues;
    const _enableReinitialize = props.enableReinitialize ?? false;

    return (
        <Formik<TValues>
            initialValues={_initialValues}
            validationSchema={props.validationSchema}
            onSubmit={(values) => {
                props.onSubmit?.(values);
            }}
            enableReinitialize={_enableReinitialize}
            innerRef={props.formikRef}
        >
            {(formik) => (
                <FormikForm {...(pickFormDomProps(props) as unknown as StrapFormProps)}>
                    {typeof props.children === "function"
                        ? props.children(formik)
                        : props.children}
                </FormikForm>
            )}
        </Formik>
    );
};

export default Object.assign(Form, {
    Autocomplete: Controls.FormAutocomplete,
    Button: Controls.FormButton,
    Checkbox: Controls.FormCheckbox,
    Color: Controls.FormColor,
    ColorRow: Controls.FormColorRow,
    Date: Controls.FormDate,
    Datetime: Controls.FormDatetime,
    Dropdown: Controls.FormDropdown,
    SelectRow: Controls.FormSelectRow,
    Email: Controls.FormEmail,
    FieldRow: Controls.FormFieldRow,
    File: Controls.FormFile,
    Group: Controls.FormGroup,
    Hidden: Controls.FormHidden,
    Attachments: Controls.FormAttachments,
    Image: Controls.FormImage,
    ImageCrop: Controls.FormImageCrop,
    Icon: Controls.FormIconPicker,
    Input: Controls.FormInput,
    Label: Controls.FormLabel,
    Month: Controls.FormMonth,
    Number: Controls.FormNumber,
    NumberRow: Controls.FormNumberRow,
    PanelSection: Controls.FormPanelSection,
    Password: Controls.FormPassword,
    Phone: Controls.FormPhone,
    Radio: Controls.FormRadio,
    Range: Controls.FormRange,
    Search: Controls.FormSearch,
    Section: Controls.FormSection,
    Switch: Controls.FormSwitch,
    SwitchRow: Controls.FormSwitchRow,
    Text: Controls.FormText,
    TextRow: Controls.FormTextRow,
    TextArea: Controls.FormTextArea,
    Time: Controls.FormTime,
    Url: Controls.FormUrl,
    Week: Controls.FormWeek,
    Zip: Controls.FormZip,
    Currency: Controls.FormCurrency
});
